import { useState } from 'react';
import { Calendar, ChevronRight } from 'lucide-react';
import type { CustomerDiagnosticReport } from './types';

const PAGE_SIZE = 10;

interface DiagnosticsTabProps {
    diagnostics: CustomerDiagnosticReport[];
    onOpenDiagnostic: (report: CustomerDiagnosticReport) => void;
}

export function DiagnosticsTab({ diagnostics, onOpenDiagnostic }: DiagnosticsTabProps) {
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const visible = diagnostics.slice(0, visibleCount);

    return (
        <section className="bg-panel border border-line rounded-xl overflow-hidden">
            <div className="p-4 border-b border-line flex items-center justify-between bg-raised">
                <h3 className="font-bold text-fg flex items-center gap-2">
                    Diagnostics
                </h3>
                {diagnostics.length > 0 && (
                    <span className="text-xs text-fg-4">
                        {diagnostics.length} report{diagnostics.length !== 1 ? 's' : ''}
                    </span>
                )}
            </div>
            <div className="divide-y divide-tint/5">
                {diagnostics.length === 0 ? (
                    <div className="p-8 text-center text-fg-4 text-sm">
                        No diagnostics recorded for this customer yet.
                    </div>
                ) : (
                    visible.map((rep) => {
                        const findings = Array.isArray(rep.findings) ? rep.findings : [];
                        const critical = findings.filter((f) => f.severity === 'critical').length;
                        return (
                            <button
                                key={rep.id}
                                onClick={() => onOpenDiagnostic(rep)}
                                className="w-full p-4 hover:bg-tint/5 transition-colors text-left flex items-start gap-3"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 text-xs text-fg-3">
                                        <Calendar className="w-3.5 h-3.5 text-fg-4" />
                                        {new Date(rep.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        {rep.vehicle_vrm && (
                                            <span className="font-mono bg-raised text-fg-2 px-1.5 py-0.5 rounded border border-tint/10">
                                                {rep.vehicle_vrm}
                                            </span>
                                        )}
                                    </div>
                                    <h4 className="font-bold text-fg mt-1 truncate">
                                        {findings.length} finding{findings.length === 1 ? '' : 's'}
                                        {critical > 0 && <span className="text-danger"> · {critical} critical</span>}
                                    </h4>
                                    {rep.summary && (
                                        <p className="text-sm text-fg-3 line-clamp-2 mt-0.5">{rep.summary}</p>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <span className={`text-fd-3xs font-bold uppercase tracking-widest px-2 py-0.5 rounded ${
                                        rep.status === 'quoted'
                                            ? 'bg-success/10 text-success'
                                            : rep.status === 'complete'
                                              ? 'bg-tint/5 text-fg-3'
                                              : 'bg-gold-fill/10 text-gold'
                                    }`}>
                                        {rep.status === 'in_progress' ? 'In progress' : rep.status}
                                    </span>
                                    <ChevronRight className="w-4 h-4 text-fg-4" />
                                </div>
                            </button>
                        );
                    })
                )}
            </div>
            {diagnostics.length > visibleCount && (
                <button
                    onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                    className="w-full p-3 text-xs font-bold text-accent hover:bg-tint/5 transition-colors border-t border-tint/5"
                >
                    Load more ({diagnostics.length - visibleCount} remaining)
                </button>
            )}
        </section>
    );
}
